'use client';

import type { MouseEvent } from 'react';

import { buildListingPaginationHref } from '@/features/seo-listing/utils/parse-listing-page-param';

type ListingPaginationSearchParams = Record<string, string | string[] | undefined>;

type ListingPaginationProps = {
  pathname: string;
  searchParams: ListingPaginationSearchParams;
  currentPage: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
};

/** Matches former outline Button look used in the listing grid. */
const PAGINATION_CONTROL_CLASS =
  'inline-flex items-center justify-center whitespace-nowrap rounded-md border border-input bg-white px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

const PAGINATION_DISABLED_CLASS =
  'inline-flex items-center justify-center whitespace-nowrap rounded-md border border-input bg-white px-4 py-2 text-sm font-medium opacity-50 pointer-events-none';

/** Current page digit is an <a> for crawlers; looks like plain text. */
const CURRENT_PAGE_LINK_CLASS = 'text-inherit no-underline pointer-events-none';

export const ListingPagination = ({
  pathname,
  searchParams,
  currentPage,
  totalPages,
  onPageChange,
}: ListingPaginationProps) => {
  if (totalPages <= 1) {
    return null;
  }

  const previousHref =
    currentPage > 1 ? buildListingPaginationHref(pathname, searchParams, currentPage - 1) : null;
  const nextHref =
    currentPage < totalPages
      ? buildListingPaginationHref(pathname, searchParams, currentPage + 1)
      : null;
  const currentPageHref = buildListingPaginationHref(pathname, searchParams, currentPage);

  const handleLinkClick = (page: number, event: MouseEvent<HTMLAnchorElement>) => {
    // Modified clicks keep native navigation (new tab). `event.button` is omitted:
    // on some browsers it is not 0 for a primary click, which skipped the handler
    // and, together with a same-document href, left the URL unchanged.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    onPageChange?.(page);
  };

  return (
    <nav className="mt-8 flex justify-center items-center gap-2" aria-label="Пагинация">
      {previousHref ? (
        <a
          href={previousHref}
          className={PAGINATION_CONTROL_CLASS}
          rel="prev"
          data-listing-pagination="true"
          onClick={(event) => handleLinkClick(currentPage - 1, event)}
        >
          Предыдущая
        </a>
      ) : (
        <span className={PAGINATION_DISABLED_CLASS} aria-disabled="true">
          Предыдущая
        </span>
      )}

      <span className="px-4 py-2 text-sm text-gray-600">
        Страница{' '}
        <a
          href={currentPageHref}
          className={CURRENT_PAGE_LINK_CLASS}
          data-listing-pagination="true"
          aria-current="page"
          tabIndex={-1}
        >
          {currentPage}
        </a>{' '}
        из {totalPages}
      </span>

      {nextHref ? (
        <a
          href={nextHref}
          className={PAGINATION_CONTROL_CLASS}
          rel="next"
          data-listing-pagination="true"
          onClick={(event) => handleLinkClick(currentPage + 1, event)}
        >
          Следующая
        </a>
      ) : (
        <span className={PAGINATION_DISABLED_CLASS} aria-disabled="true">
          Следующая
        </span>
      )}
    </nav>
  );
};
